import React, { useContext, useEffect } from 'react'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Button, Grid, Header, Segment } from 'semantic-ui-react'
import CropChange from './CropChange'
import { FormFields } from '~/components/FormFields'
import { CoolFarmCropOptionsContext } from '../../..'
import dayjs from 'dayjs'

const NATIVE_FOREST_KEY = 6

const CropPriorChanges: React.FC = () => {
  const { t } = useTranslation()
  const { control, getValues, setValue } = useFormContext()
  const options = useContext(CoolFarmCropOptionsContext)

  const { fields: carbonFields, append: appendCarbon, remove: removeCarbon } = useFieldArray({
    control,
    name: 'carbonChanges',
    keyName: 'key'
  })

  const forestTypes = options?.forestTypes?.map((type: any) => ({
    key: type.id || type.key,
    value: type.id || type.key,
    text: type.label
  })) || []

  const watchCarbonLastItem = useWatch({ control, name: `carbonChanges[${(carbonFields || []).length - 1}]` }) || {}
  const watchHasTreeBiomas = useWatch({ control, name: 'hasTreeBiomass' })
  const watchHarvestingYear = useWatch({ control, name: 'harvestingYear' })

  const hasNativeForrest = watchCarbonLastItem?.landUseFrom === NATIVE_FOREST_KEY
  const minYear = dayjs().year() - 20;
  const lastItemEqualMinYear = parseInt(watchCarbonLastItem.year) === minYear

  useEffect(() => {
    if(!hasNativeForrest || !watchHasTreeBiomas) {
      setValue('forestType', '')
      setValue('forestAge', '')
    }

  }, [hasNativeForrest, watchHasTreeBiomas])

  function renderNativeForest() {
    const carbonChanges = getValues('carbonChanges')
    const carbonWithNoNativeForest = carbonChanges.find(carbon => carbon?.landUseFrom !== NATIVE_FOREST_KEY)
    return <Segment>
      <Header as='h4' style={{ marginTop: '1em', marginBottom: '1em' }}>
        {t('Forest Biomass')}
      </Header>
      <FormFields.Checkbox
        control={control}
        label={t(' If the above mentioned (most recent) forest change occurred within the last 20 years, please enter the type of forest, and for a change out of a forest, the forest age.')}
        name={'hasTreeBiomass'}
        defaultValue={0}
        checkboxValue={1}
      />
      {Boolean(watchHasTreeBiomas) && <Grid>
        <Grid.Row>
          <Grid.Column width={8}>
            <FormFields.Select
              control={control}
              label={t('Forest type')}
              name={'forestType'}
              rules={{ required: true }}
              withoutAsterisk
              options={forestTypes}
              placeholder={t('Select')}
              noErrorMessage
            />
          </Grid.Column>
          {!carbonWithNoNativeForest && <Grid.Column width={8}>
            <FormFields.Input
              control={control}
              name={'forestAge'}
              withoutAsterisk
              placeholder={t('Enter forest age')}
              label={t('Forest age')}
              typeInput='controled'
              noErrorMessage
            />
          </Grid.Column>}
        </Grid.Row>
      </Grid>}
    </Segment>
  }

  return <div style={{ display: 'flex', flexDirection: 'column', gap: '1em' }}>
    <Header as='h4' style={{ marginTop: '1em', marginBottom: '1em' }}>
      {t('Current Management Practices')}
    </Header>
    {carbonFields.map((carbonField, index) => {
      const isLastRow = (carbonFields.length - 1) === index
      const maxYear = carbonFields.length > 1 && isLastRow ? parseInt(carbonFields[index - 1]['year']) - 1 : watchHarvestingYear
      return <CropChange
        isLastRow={isLastRow}
        index={index}
        removeCarbon={removeCarbon}
        key={carbonField.key}
        maxYear={maxYear}
      />
    })}

    <div style={{ display: 'flex', justifyContent: 'end', marginTop: '1em' }}>
      <Button
        type='button'
        icon='plus'
        disabled={Object.values(watchCarbonLastItem).findIndex(value => value === '') >= 0 || lastItemEqualMinYear}
        style={{ color: 'white' }}
        content={t('Add Prior Change')}
        onClick={() => {
          const carbonChanges = getValues('carbonChanges') || []
          const priorChange = carbonChanges.length > 0 ?
            carbonChanges[carbonChanges.length - 1] : { year: watchHarvestingYear + 1, landUseFrom: '', tillageTypeFrom: '' }

          appendCarbon({
            year: parseInt(priorChange['year']) - 1,
            percentage: '100',
            landUseFrom: '',
            landUseTo: priorChange['landUseFrom'],
            tillageTypeFrom: '',
            tillageTypeTo: priorChange['tillageTypeFrom'],
            carbonInputFrom: '',
            carbonInputTo: priorChange['carbonInputFrom']
          })
        }}
      />
    </div>
    {hasNativeForrest && renderNativeForest()}
  </div>
}
export default CropPriorChanges